import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { NOTIFICATION_STATUS, NOTIFICATION_STATUS_TEXT, NOTIFICATION_TYPE } from "@/utils/constants";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { toast } from 'react-toastify';
import DatePicker, { registerLocale } from "react-datepicker";
import { ja } from 'date-fns/locale/ja';
import { format } from "date-fns";
import React from "react";
registerLocale('ja', ja)

DataTable.use(DT);

const createNotificationSchema = yup.object().shape({
  title: yup.string().required("タイトルが必須です。"),
  content: yup.string().required("通知内容が必須です。"),
  type: yup.string().required("通知種別が必須です。"),
  immediateSend: yup.boolean(),
  pushTime: yup.date().when("immediateSend", (immediateSend) => {
    if (!immediateSend[0]) {
      return yup.date().required("送信予定時刻を指定するか即時送信にしてください。")
    }
    return yup.date().nullable();
  }),
});

const editNotificationSchema = yup.object().shape({
  title: yup.string().required("タイトルが必須です。"),
  content: yup.string().required("通知内容が必須です。"),
  type: yup.string().required("通知種別が必須です。"),
  pushTime: yup.date().required("送信予定時刻を指定するか即時送信にしてください。"),
});

const Notifications = () => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  const [notifications, setNotifications] = useState([]);
  const [notificationSearch, setNotificationSearch] = useState<string>("");
  const [searchStatus, setSearchStatus] = useState<string>("");
  const [searchType, setSearchType] = useState<string>("");
  const [activeNotification, setActiveNotification] = useState<any>(null);
  const [pushTime, setPushTime] = useState<Date>(null);

  const createForm = useForm({
    resolver: yupResolver(createNotificationSchema),
    values: {
      pushTime: pushTime,
    },
    defaultValues: {
      title: "",
      content: "",
      type: "",
      immediateSend: false,
      pushTime: new Date(),
    },
  });

  const editForm = useForm({
    resolver: yupResolver(editNotificationSchema),
    values: {
      title: activeNotification?.title,
      pushTime: activeNotification?.pushTime,
      content: activeNotification?.content,
      type: activeNotification?.type,
    },
    defaultValues: {
      title: activeNotification?.title || "",
      content: activeNotification?.content || "",
      type: activeNotification?.type || "",
      pushTime: activeNotification?.pushTime || "",
    },
  });

  const handleRegisterNotification = async (data: any) => {
    if (!data?.immediateSend) {
      if (data?.pushTime && data.pushTime < new Date()) {
        toast.error("送信予定は過去の時間になっています。");
        return;
      }
    }

    const confirmed = data?.immediateSend ? window.confirm("通知を即時に送信します。よろしいでしょうか") : true;
    if (confirmed) {
      try {
        dispatch(setIsApiLoading(true));
        await API.post(`notification`, {
          ...data,
          pushTime: data?.pushTime ? data?.pushTime.toISOString() : "",
        });
        toast.success("通知が登録されました。");
        document.querySelector<HTMLButtonElement>("#modalCreateNotification > div > div > div.modal-header > button")?.click();
        fetchNotifications();
      } catch (err) {
        console.error("通知登録に失敗しました。もう一度お試しください。");
      } finally {
        dispatch(setIsApiLoading(false));
      }
    }
  };

  const handleEditNotification = async (data: any) => {
    if (!data?.immediateSend) {
      if (data?.pushTime && data.pushTime < new Date()) {
        toast.error("送信予定は過去の時間になっています。");
        return;
      }
    }

    try {
      dispatch(setIsApiLoading(true));
      await API.put(`notification/${activeNotification?.id}`, {
        ...data,
        pushTime: data?.pushTime ? data?.pushTime.toISOString() : "",
      });
      toast.success("通知が変更されました。");
      document.querySelector<HTMLButtonElement>("#modalEditNotification > div > div > div.modal-header > button")?.click();
      fetchNotifications();
    } catch (err) {
      console.error("通知の更新に失敗しました。もう一度お試しください。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeleteNotification = async () => {
    try {
      if (window.confirm("通知を削除します。よろしいでしょうか")) {
        dispatch(setIsApiLoading(true));
        await API.delete(`notification/${activeNotification?.id}`);
        toast.success("通知が削除されました。");
        document.querySelector<HTMLButtonElement>("#modalEditNotification > div > div > div.modal-header > button")?.click();
        fetchNotifications();
      }
    } catch (err) {
      console.error("通知の削除に失敗しました。もう一度お試しください。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const fetchNotifications = async () => {
    dispatch(setIsLoading(true));
    try {
      const params = new URLSearchParams();
      params.append("notification", notificationSearch.trim());
      params.append("status", searchStatus);
      params.append("type", searchType);
      const response = await API.get(`notifications?${params.toString()}`);
      const { data } = response.data;
      const convertData = data.map((item: any) => ({
        ...item,
        completedTime: item.completedTime ? format(new Date(item.completedTime).toLocaleString(), "yyyy/MM/dd HH:mm:ss") : "",
        pushTime: item.pushTime ? format(new Date(item.pushTime).toLocaleString(), "yyyy/MM/dd HH:mm:ss") : "",
        addDate: item.addDate ? format(new Date(item.addDate).toLocaleString(), "yyyy/MM/dd HH:mm:ss") : "",
      }))
      setNotifications(convertData);
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  return (
    <>
      <Helmet>
        <title>通知管理</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between">
              <div className="form-search row g-2 align-items-center">
                <div className="col-auto">
                  <label htmlFor="notificationSearch" className="form-label mb-0">
                    通知検索
                  </label>
                  <input
                    type="text"
                    id="notificationSearch"
                    className="form-select"
                    placeholder="検索..."
                    value={notificationSearch}
                    onChange={(e) => setNotificationSearch(e.target.value)}
                  />
                </div>
                <div className="col-auto">
                  <label htmlFor="searchStatus" className="form-label mb-0">
                    通知種別
                  </label>
                  <select
                    id="searchType"
                    className="form-select"
                    defaultValue="0"
                    value={searchType}
                    onChange={(e) => {
                      setSearchType(e.target.value);
                    }}
                  >
                    {
                      NOTIFICATION_TYPE.map((status) => (
                        <option value={status.value}>{status.label}</option>
                      ))
                    }
                  </select>
                </div>
                <div className="col-auto">
                  <label htmlFor="searchType" className="form-label mb-0">
                    ステータス
                  </label>
                  <select
                    id="searchStatus"
                    className="form-select"
                    defaultValue="0"
                    value={searchStatus}
                    onChange={(e) => {
                      setSearchStatus(e.target.value);
                    }}
                  >
                    {
                      NOTIFICATION_STATUS.map((status) => (
                        <option value={status.value}>{status.label}</option>
                      ))
                    }
                  </select>
                </div>
                <div className="col-auto">
                  <label
                    htmlFor="status"
                    className="form-label mb-0"
                    style={{ color: "#fff" }}
                  >
                    &nbsp;
                  </label>
                  <button
                    className="btn_submit btn btn-primary"
                    onClick={fetchNotifications}
                  >
                    検索
                  </button>
                </div>
              </div>
              <div className="form-search row g-2 align-items-center">
                <div className="col-auto">
                  <label
                    htmlFor="status"
                    className="form-label mb-0"
                    style={{ color: "#fff" }}
                  >
                    &nbsp;
                  </label>
                  <button
                    className="btn_submit btn btn-danger"
                    data-bs-toggle="modal"
                    data-bs-target="#modalCreateNotification"
                  >
                    <i className="fas fa-plus" />
                    &nbsp;&nbsp;作成
                  </button>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <div
                    id="example3_wrapper"
                    className="dataTables_wrapper no-footer"
                  >
                    <Loading />
                    {!loading && (
                      <DataTable
                        data={notifications}
                        className="display"
                        options={{
                          autoWidth: true,
                          rowCallback: (row, data) => {
                            (row as HTMLTableRowElement).onclick = () =>
                              setActiveNotification(data);
                          },
                          stripeClasses: [],
                          order: [[4, 'desc']],
                          searching: false,
                          destroy: true,
                          info: false,
                          paging: true,
                          language: {
                            lengthMenu: "レコード数&nbsp;_MENU_",
                            emptyTable: "データがありません。",
                          },
                          layout: {
                            topStart: null,
                            topEnd: null,
                            bottomStart: "pageLength",
                            bottomEnd: "paging",
                          },
                          columnDefs: [
                            { targets: "_all", className: "text-start" },
                          ],
                        }}
                        columns={[
                          {
                            title: "View",
                            className: "block_mb",
                            orderable: false,
                            data: null,
                            render: (data, type, row) => {
                              return `<i class="flaticon-381-add-1" data-bs-toggle="modal" data-bs-target="#modalDetailNotification"></i>`;
                            },
                          },
                          {
                            title: "タイトル",
                            data: "title",
                            orderable: true,
                            className: "text-start",
                          },
                          {
                            title: "通知種別",
                            data: "typeText",
                            className: "mb_none text-start",
                            orderable: true,
                          },
                          {
                            title: "ステータス",
                            data: "statusText",
                            className: "text-start",
                          },
                          {
                            title: "送信完了時間",
                            data: "completedTime",
                            className: "mb_none text-start",
                            orderable: true,
                          },
                          {
                            title: "登録時間",
                            data: "addDate",
                            className: "mb_none text-start",
                            orderable: true,
                          },
                          {
                            orderable: false,
                            className: "mb_none",
                            data: null,
                            render: (data, type, row) => {
                              return `<div class="d-flex justify-content-center align-items-center">
                                <button class="btn btn-primary shadow btn-xs sharp me-1" data-bs-toggle="modal" data-bs-target="#modalDetailNotification"><i class="fa fa-pencil"></i></button>
                              </div>`;
                            },
                          },
                        ]}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        id="modalCreateNotification"
        title={<h4 className="modal-title fw-bold">通知登録</h4>}
        onClose={() => {
          setPushTime(new Date());
          createForm.reset();
        }}
      >
        <Form onSubmit={createForm.handleSubmit(handleRegisterNotification)}>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3">
              タイトル&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <Form.Control
                type="text"
                placeholder="タイトルを入力してください"
                isInvalid={!!createForm.formState.errors.title}
                {...createForm.register("title")}
              />
              <Form.Control.Feedback type="invalid">
                {createForm.formState.errors.title?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3">
              通知内容&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="通知内容を入力してください"
                isInvalid={!!createForm.formState.errors.content}
                {...createForm.register("content")}
              />
              <Form.Control.Feedback type="invalid">
                {createForm.formState.errors.content?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3">
              通知種別&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <Form.Select
                isInvalid={!!createForm.formState.errors.type}
                defaultValue="0"
                {...createForm.register("type")}
              >
                {NOTIFICATION_TYPE.map((item) => (
                  <option value={item.value} key={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {createForm.formState.errors.type?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3 has-datepicker">
            <Form.Label column sm="3" md="3">
              送信予定&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <DatePicker
                showIcon
                minDate={new Date()}
                minTime={new Date()}
                toggleCalendarOnIconClick
                selected={pushTime}
                showTimeInput
                timeInputLabel="Time:"
                locale={ja}
                placeholderText="yyyy/mm/dd hh:mm"
                dateFormat="yyyy/MM/dd hh:mm"
                className={`form-control ${createForm.formState.errors.pushTime && !pushTime ? "red-border" : ""}`}
                onChange={(date) => {
                  setPushTime(date);
                }}
              />
              <div className="invalid-feedback">
                {createForm.formState.errors.pushTime && !pushTime ? createForm.formState.errors.pushTime?.message : ""}
              </div>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3"> </Form.Label>
            <Col sm="9">
              <Form.Check
                label="即時送信"
                {...createForm.register("immediateSend")}
              >
              </Form.Check>
            </Col>
          </Form.Group>
          <div className="btn_group text-center">
            <button type="submit" className="btn btnStand btn_save btn-primary">
              登録
            </button>
          </div>
        </Form>
      </Modal>
      <Modal
        id="modalEditNotification"
        title={
          <>
            <h4 className="modal-title fw-bold">通知編集</h4>
            <p>通知ID：{activeNotification?.id}</p>
          </>
        }
        onClose={() => {
          editForm.reset();
        }}
      >
        <Form onSubmit={editForm.handleSubmit(handleEditNotification)}>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3">
              タイトル&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <Form.Control
                type="text"
                onChange={(e) => { setActiveNotification({ ...activeNotification, title: e.target.value }) }}
                placeholder="タイトルを入力してください"
                isInvalid={!!editForm.formState.errors.title}
                {...editForm.register("title")}
              />
              <Form.Control.Feedback type="invalid">
                {editForm.formState.errors.title?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3">
              通知内容&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <Form.Control
                as="textarea"
                rows={3}
                onChange={(e) => { setActiveNotification({ ...activeNotification, content: e.target.value }) }}
                placeholder="通知内容を入力してください"
                isInvalid={!!editForm.formState.errors.content}
                {...editForm.register("content")}
              />
              <Form.Control.Feedback type="invalid">
                {editForm.formState.errors.content?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3">
              通知種別&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <Form.Select
                isInvalid={!!editForm.formState.errors.type}
                defaultValue={activeNotification?.type}
                {...editForm.register("type")}
              >
                {NOTIFICATION_TYPE.map((item) => (
                  <option value={item.value} key={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {editForm.formState.errors.type?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3">
              ステータス&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <Form.Control
                plaintext
                readOnly
                defaultValue={NOTIFICATION_STATUS_TEXT[activeNotification?.status]}
              />
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3 has-datepicker">
            <Form.Label column sm="3" md="3">
              送信予定&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <DatePicker
                showIcon
                toggleCalendarOnIconClick
                selected={activeNotification?.pushTime ? new Date(activeNotification?.pushTime) : new Date()}
                showTimeInput
                timeInputLabel="Time:"
                locale={ja}
                placeholderText="yyyy/mm/dd hh:mm"
                dateFormat="yyyy/MM/dd hh:mm"
                className={`form-control ${editForm.formState.errors.pushTime && !activeNotification?.pushTime ? "red-border" : ""}`}
                onChange={(date) => {
                  setActiveNotification({
                    ...activeNotification,
                    pushTime: date,
                  });
                }}
              />
              <div className="invalid-feedback">
                {editForm.formState.errors.pushTime && !activeNotification?.pushTime ? editForm.formState.errors.pushTime?.message : ""}
              </div>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="3" md="3">
              登録時間&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;：
            </Form.Label>
            <Col sm="9">
              <Form.Control
                plaintext
                readOnly
                defaultValue={activeNotification?.addDate}
              />
            </Col>
          </Form.Group>
          <div className="btn_group text-center">
            <button type="submit" className="btn btnStand btn_save btn-warning me-2">
              保存
            </button>
            <button type="button" className="btn btn-secondary btn-danger" onClick={handleDeleteNotification}>
              通知を削除
            </button>
          </div>
        </Form>
      </Modal>
      <Modal
        id="modalDetailNotification"
        title={
          <>
            <h4 className="modal-title fw-bold">通知詳細</h4>
            <p>通知ID：{activeNotification?.id}</p>
          </>
        }
      >
        <ul className="detail_user_mobile">
          {activeNotification && (
            <>
              <li>
                <label>タイトル</label>
                <span>{activeNotification.title ?? ""}</span>
              </li>
              <li className="d-flex">
                <label>通知内容</label>
                <div>
                  {activeNotification?.content.split('\n').map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              </li>
              <li>
                <label>ステータス</label>
                <span>{activeNotification.statusText ?? ""}</span>
              </li>
              {
                activeNotification.isEdit ?
                  <li>
                    <label>送信予定</label>
                    <span>{activeNotification.pushTime ?? ""}</span>
                  </li>
                  :
                  <li>
                    <label>送信完了</label>
                    <span>{activeNotification.completedTime ?? ""}</span>
                  </li>
              }
              <li>
                <label>登録時間</label>
                <span>{activeNotification.addDate ?? ""}</span>
              </li>
              <li>
                <label></label>
                <span>
                  <div className="d-flex justify-content-center align-items-center">
                    {
                      activeNotification.isEdit &&
                      <button
                        type="button"
                        className="btn btn-warning me-2"
                        data-bs-toggle="modal"
                        data-bs-target="#modalEditNotification"
                      >
                        編集
                      </button>
                    }
                    <button
                      type="button"
                      className="btn btn-secondary btnClose"
                      data-bs-dismiss="modal"
                    >
                      閉じる
                    </button>
                  </div>
                </span>
              </li>
            </>
          )}
        </ul>
      </Modal>
    </>
  );
};

export default Notifications;
